import { BRAND_ASSETS } from "@/lib/brand-assets";
import { motion, useReducedMotion } from "framer-motion";
import { Crown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

interface ChickyLogoProps {
  subscribed: boolean;
  src?: string;
  animatedSrc?: string | null;
  alt?: string;
  className?: string;
  variant?: "default" | "ecole" | "liceo";
  condensed?: boolean;
  compactOnMobile?: boolean;
}

const GIF_ANIMATION_MS = 2000;

export function ChickyLogo({
  subscribed,
  src = BRAND_ASSETS.lockup.dark,
  animatedSrc = null,
  alt = "ChickyTutor",
  className = "h-11 w-auto md:h-12",
  variant = "default",
  condensed = false,
  compactOnMobile = false,
}: ChickyLogoProps) {
  const shouldReduceMotion = useReducedMotion();
  const [animationKey, setAnimationKey] = useState(0);
  const [isAnimatedLogoEnabled, setIsAnimatedLogoEnabled] = useState(src.endsWith(".gif"));
  const imgRef = useRef<HTMLImageElement>(null);
  const animationTimeoutRef = useRef<number | null>(null);
  const isAnimatingRef = useRef(false);
  const resolvedAnimatedSrc = animatedSrc?.endsWith(".gif") ? animatedSrc : null;
  const activeLogoSrc =
    !shouldReduceMotion && isAnimatedLogoEnabled && resolvedAnimatedSrc ? resolvedAnimatedSrc : src;
  const canRestartAnimation =
    !shouldReduceMotion && (activeLogoSrc.endsWith(".gif") || Boolean(resolvedAnimatedSrc));

  const restartAnimation = useCallback(() => {
    if (!canRestartAnimation || isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    if (resolvedAnimatedSrc && !isAnimatedLogoEnabled) {
      setIsAnimatedLogoEnabled(true);
      setAnimationKey(0);
    } else if (activeLogoSrc.endsWith(".gif")) {
      setAnimationKey(Date.now());
    }

    if (animationTimeoutRef.current !== null) {
      window.clearTimeout(animationTimeoutRef.current);
    }

    animationTimeoutRef.current = window.setTimeout(() => {
      isAnimatingRef.current = false;
      animationTimeoutRef.current = null;
    }, GIF_ANIMATION_MS);
  }, [activeLogoSrc, canRestartAnimation, isAnimatedLogoEnabled, resolvedAnimatedSrc]);

  useEffect(() => {
    if (shouldReduceMotion || !resolvedAnimatedSrc || isAnimatedLogoEnabled) return;

    const win = window as typeof window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let disposed = false;
    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const preloadAnimatedLogo = () => {
      const image = new Image();
      image.decoding = "async";
      image.onload = () => {
        if (!disposed) {
          setIsAnimatedLogoEnabled(true);
        }
      };
      image.src = resolvedAnimatedSrc;
    };

    if (typeof win.requestIdleCallback === "function") {
      idleId = win.requestIdleCallback(preloadAnimatedLogo, { timeout: 4000 });
    } else {
      timeoutId = window.setTimeout(preloadAnimatedLogo, 2500);
    }

    return () => {
      disposed = true;
      if (idleId !== null && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isAnimatedLogoEnabled, resolvedAnimatedSrc, shouldReduceMotion]);

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current !== null) {
        window.clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    restartAnimation();
  };

  const imageClassName =
    variant === "ecole"
      ? `${className} object-contain brightness-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.18)]`
      : `${className} object-contain drop-shadow-[0_6px_14px_rgba(0,0,0,0.28)]`;

  const logoImage = (
    <img
      ref={imgRef}
      src={
        activeLogoSrc.endsWith(".gif") && animationKey > 0
          ? `${activeLogoSrc}?v=${animationKey}`
          : activeLogoSrc
      }
      alt={alt}
      className={imageClassName}
      draggable={false}
    />
  );

  return (
    <motion.div
      className="relative inline-flex shrink-0 items-start gap-1 overflow-visible"
      onMouseEnter={handleMouseEnter}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.16 }}
    >
      {variant === "liceo" ? (
        <span className="relative inline-block" data-liceo-logo>
          {logoImage}
          {/* Use the supplied alpha as a mask over the lettering only. The red
              rule and blue/red emblem retain their original artwork. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-white"
            style={{
              maskImage: `url("${activeLogoSrc}")`,
              maskSize: "100% 100%",
              maskRepeat: "no-repeat",
              clipPath:
                "polygon(0 0, 56% 0, 56% 35%, 52% 35%, 52% 68%, 0 68%, 0 84%, 100% 84%, 100% 100%, 0 100%)",
            }}
          />
        </span>
      ) : compactOnMobile ? (
        <picture>
          <source media="(max-width: 767px)" srcSet={BRAND_ASSETS.mark.web} />
          {logoImage}
        </picture>
      ) : (
        logoImage
      )}
      {subscribed && (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md font-extrabold text-black shadow-lg transition-all duration-300 ${
            condensed
              ? "mt-0.5 gap-0 px-1 py-px text-[8px] md:mt-1 md:text-[9px]"
              : "mt-1 gap-0 px-1.5 py-0.5 text-[9px] md:mt-1.5 md:text-[10px]"
          } ${variant === "liceo" ? "bg-school-accent !text-white" : variant === "ecole" ? "bg-[#ffb606]" : "bg-orange-400"}`}
        >
          <Crown
            className={`mr-0.5 shrink-0 transition-all duration-300 ${condensed ? "h-2.5 w-2.5" : "h-3 w-3"}`}
          />{" "}
          PRO
        </motion.span>
      )}
    </motion.div>
  );
}
